import { useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Alert,
  AlertDescription,
} from "../components/ui/alert";
import {
  Eye,
  EyeOff,
  AlertTriangle,
  Check,
  X,
} from "lucide-react";
import logo2 from "../assets/logo2.svg";
import {
  evaluatePassword,
  evaluateStrength,
  getPasswordErrors,
  type PasswordContext,
} from "../utils/passwordPolicy";

/**
 * SetAppPasswordPage — USSD-PIN → app-password graduation (Option B, 2026-08-14).
 *
 * Shown when a non-graduated USSD-only user logs into the app with their USSD
 * PIN. The backend returned APP_PASSWORD_REQUIRED + a 10-min scoped
 * setAppPasswordToken (stashed in AuthContext.pendingAppPassword). Here the
 * user sets a strong app password; on success we log in with it and the PIN
 * becomes USSD-only.
 */
export function SetAppPasswordPage() {
  const navigate = useNavigate();
  const { pendingAppPassword, setAppPassword, login, isLoading } = useAuth();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Context for the strong-password policy (no ID number available here — the
  // backend re-validates with the full context including idNumber).
  const passwordContext: PasswordContext = useMemo(
    () => ({
      firstName: pendingAppPassword?.firstName ?? undefined,
      lastName: pendingAppPassword?.lastName ?? undefined,
      phoneNumber: pendingAppPassword?.phoneNumber,
    }),
    [pendingAppPassword],
  );

  const checks = evaluatePassword(password);
  const strength = evaluateStrength(password, passwordContext);
  const passwordOk = checks.isValid && strength.ok;
  const confirmOk = confirmPassword.length > 0 && confirmPassword === password;
  const canSubmit = passwordOk && confirmOk && !submitting && !isLoading;

  // No active graduation session → back to login.
  if (!pendingAppPassword) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!passwordOk) {
      setError(strength.errors[0] || "Please choose a stronger password.");
      return;
    }
    if (!confirmOk) {
      setError("New password and confirmation do not match.");
      return;
    }

    setSubmitting(true);
    try {
      // 1. Graduate: set the password, flip app_password_set, bump tokenVersion.
      await setAppPassword(password, confirmPassword);
      // 2. Log in with the new password (scoped token is consumed).
      await login({ identifier: pendingAppPassword.phoneNumber, password });
      navigate("/dashboard");
    } catch (err: any) {
      // If graduation succeeded but the auto-login failed (e.g. token race),
      // send the user to /login with the new password rather than blocking.
      const msg = err?.message || "Setting your app password failed. Please try again.";
      if (/set-app-password/i.test(msg) || /expired|invalid/i.test(msg)) {
        setError(msg);
      } else {
        // Graduation likely succeeded; prompt a normal login.
        setError(`${msg} Your app password was set — please sign in with it.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const baseErrors = getPasswordErrors(password);
  const allErrors = [...baseErrors, ...strength.errors];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#86BE41] to-[#2D8CCA]">
      <div className="mobile-container">
        <div
          style={{
            padding: "var(--mobile-padding)",
            paddingTop: "2rem",
            paddingBottom: "2rem",
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-2">
              <img
                src={logo2}
                alt="MyMoolah Logo"
                className="h-16 w-auto"
                style={{ height: "6rem", width: "auto", maxWidth: "300px", objectFit: "contain" }}
              />
            </div>
            <h1
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "clamp(1.5rem, 4vw, 2rem)",
                fontWeight: "var(--font-weight-bold)",
                color: "white",
                marginBottom: "0.5rem",
              }}
            >
              Set your app password
            </h1>
            <p
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "var(--mobile-font-base)",
                color: "rgba(255, 255, 255, 0.9)",
              }}
            >
              You signed in with your USSD PIN. Please create an app password to finish logging in.
            </p>
          </div>

          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-xl" style={{ marginTop: "-2rem" }}>
            <CardHeader style={{ paddingBottom: "1rem" }}>
              <CardTitle
                className="text-center"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "clamp(1.125rem, 2.5vw, 1.25rem)",
                  fontWeight: "var(--font-weight-bold)",
                  color: "#1f2937",
                }}
              >
                Create app password
              </CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert className="border-red-200 bg-red-50 mb-4">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      color: "#dc2626",
                    }}
                  >
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p style={{ fontFamily: "Montserrat, sans-serif", fontSize: "var(--mobile-font-small)", color: "#6b7280" }}>
                  Mobile number
                </p>
                <p style={{ fontFamily: "Montserrat, sans-serif", fontSize: "var(--mobile-font-base)", fontWeight: "var(--font-weight-medium)", color: "#1f2937" }}>
                  {pendingAppPassword.phoneNumber}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* New password */}
                <div className="space-y-2">
                  <Label
                    htmlFor="newPassword"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    New app password
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter a strong password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (error) setError("");
                      }}
                      className="bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pr-12"
                      style={{
                        height: "var(--mobile-touch-target)",
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-base)",
                        borderRadius: "var(--mobile-border-radius)",
                      }}
                      required
                      aria-describedby="new-password-help"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      style={{ minHeight: "var(--mobile-touch-target)", minWidth: "var(--mobile-touch-target)", display: "flex", alignItems: "center", justifyContent: "center" }}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Live strength / policy feedback */}
                  {password.length > 0 && (
                    <ul
                      id="new-password-help"
                      className="mt-2 space-y-1"
                      style={{ fontFamily: "Montserrat, sans-serif", fontSize: "var(--mobile-font-small)" }}
                    >
                      {[
                        { label: "At least 8 characters", ok: checks.checks.minLength },
                        { label: "Uppercase + lowercase", ok: checks.checks.hasUppercase && checks.checks.hasLowercase },
                        { label: "At least one number", ok: checks.checks.hasNumber },
                        { label: "At least one symbol", ok: checks.checks.hasSpecialChar },
                        { label: "Not your name, phone, or ID", ok: strength.ok || checks.isValid === false ? strength.ok : true },
                      ].map((item) => (
                        <li key={item.label} className="flex items-center gap-1.5">
                          {item.ok ? (
                            <Check className="w-3 h-3 text-green-600" />
                          ) : (
                            <X className="w-3 h-3 text-red-500" />
                          )}
                          <span style={{ color: item.ok ? "#16a34a" : "#dc2626" }}>{item.label}</span>
                        </li>
                      ))}
                      {allErrors.length > 0 && (
                        <li className="flex items-start gap-1.5 pt-1">
                          <AlertTriangle className="w-3 h-3 text-amber-600 mt-0.5" />
                          <span style={{ color: "#b45309" }}>{allErrors[0]}</span>
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                {/* Confirm password */}
                <div className="space-y-2">
                  <Label
                    htmlFor="confirmPassword"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    Confirm app password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirm ? "text" : "password"}
                      placeholder="Re-enter your new password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (error) setError("");
                      }}
                      className={`bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pr-12 ${
                        confirmPassword.length > 0
                          ? confirmOk
                            ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                            : "border-red-300 focus:border-red-500 focus:ring-red-500"
                          : ""
                      }`}
                      style={{
                        height: "var(--mobile-touch-target)",
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-base)",
                        borderRadius: "var(--mobile-border-radius)",
                      }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      style={{ minHeight: "var(--mobile-touch-target)", minWidth: "var(--mobile-touch-target)", display: "flex", alignItems: "center", justifyContent: "center" }}
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && !confirmOk && (
                    <p style={{ fontFamily: "Montserrat, sans-serif", fontSize: "var(--mobile-font-small)", color: "#dc2626" }}>
                      Passwords do not match.
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] hover:from-[#7AB139] hover:to-[#2680B8] text-white disabled:opacity-60"
                  style={{
                    height: "var(--mobile-touch-target)",
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "var(--mobile-font-base)",
                    fontWeight: "var(--font-weight-medium)",
                    borderRadius: "var(--mobile-border-radius)",
                  }}
                >
                  {submitting ? "Setting password..." : "Set app password & sign in"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-xs text-[#2D8CCA] hover:text-[#2680B8] underline"
                  style={{ fontFamily: "Montserrat, sans-serif", fontSize: "var(--mobile-font-small)" }}
                >
                  Back to sign in
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
